import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Gantt, Tooltip, Willow } from "@svar-ui/react-gantt";
import "@svar-ui/react-gantt/all.css";
import { Radio, Spin, Empty, Segmented, Dropdown, Button, Select, Input } from "antd";
import { MoreOutlined, DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import Header from "../_shared/components/Header";
import { useGetJobsGanttQuery } from "../../../redux/api/scheduling";
import { formatDateTime } from "../_shared/utils/dateUtils";
import { env } from "../../../env";
import JobTimeline, { type TimelineHandle } from "./JobTimeline";
import { focusDate, ganttRange, useDebouncedValue, useNow } from "./scheduleFocus";

const NOW_TICK_MS = 5 * 60 * 1000;

const PALETTE = [
  "#1677ff",
  "#52c41a",
  "#fa8c16",
  "#eb2f96",
  "#722ed1",
  "#13c2c2",
  "#faad14",
  "#2f54eb",
  "#f5222d",
  "#a0d911",
];

function TaskTooltip({ data }: { data: any }) {
  if (!data) return null;
  const fmt = (v: any) =>
    v ? formatDateTime(v, "MM/DD/YYYY HH:mm") : "\u2014";

  return (
    <div style={{ padding: "6px 10px", fontSize: 13, lineHeight: 1.6 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{data.text}</div>
      {data.type === "summary" ? (
        <div>
          Due:{" "}
          {data.dueDate
            ? formatDateTime(data.dueDate, "MM/DD/YYYY HH:mm")
            : "\u2014"}
        </div>
      ) : (
        <>
          {data.taskCode && <div>Task ID: {data.taskCode}</div>}
          <div>Start: {fmt(data.start)}</div>
          {data.type !== "milestone" && <div>End: {fmt(data.end)}</div>}
          {data.staffName && <div>Staff: {data.staffName}</div>}
          {data.machineName && <div>Machine: {data.machineName}</div>}
          {data.latestStart && (
            <div>Latest Start: {fmt(data.latestStart)}</div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Center the SVAR chart on a date. `scroll-chart` with a date left-aligns it,
 * so the offset is computed the same way the library does and then shifted by
 * half the chart width. The chart measures itself after mount, so retry a few
 * frames.
 */
function centerGanttOn(api: any, date: Date) {
  let tries = 0;
  const run = () => {
    const s = api?.getState?.();
    if (!s?._scales || !s._start || !s._chartWidth) {
      if (tries++ < 30) requestAnimationFrame(run);
      return;
    }
    const x = s._scales.diff(date, s._start, "hour") * s.cellWidth;
    if (!Number.isFinite(x)) return;
    api.exec("scroll-chart", { left: Math.max(0, x - s._chartWidth / 2) });
  };
  requestAnimationFrame(run);
}

const SCALE_PRESETS: Record<string, any[]> = {
  day: [
    { unit: "month", step: 1, format: "%F %Y" },
    { unit: "day", step: 1, format: "%j" },
  ],
  week: [
    { unit: "month", step: 1, format: "%F %Y" },
    { unit: "week", step: 1, format: "'week' %W" },
  ],
  month: [
    { unit: "year", step: 1, format: "%Y" },
    { unit: "month", step: 1, format: "%F" },
  ],
};

export default function JobSchedule() {
  const { data: jobs, isLoading } = useGetJobsGanttQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
  });
  const apiRef = useRef<any>(null);
  const [scaleView, setScaleView] = useState<"day" | "week" | "month">("day");
  const [isZoomed, setIsZoomed] = useState(false);
  const zoomRef = useRef<TimelineHandle | null>(null);
  const [viewMode, setViewMode] = useState<"Timeline" | "Gantt">("Timeline");
  const [machineFilter, setMachineFilter] = useState<string | null>(null);
  const [staffFilter, setStaffFilter] = useState<string | null>(null);
  const [jobSearch, setJobSearch] = useState("");

  // Derive machine/staff options from actual scheduled data (only shows what's in use)
  const { machineOptions, staffOptions } = useMemo(() => {
    if (!jobs?.length) return { machineOptions: [], staffOptions: [] };
    const machineSet = new Set<string>();
    const staffSet = new Set<string>();
    (jobs as any[]).forEach((job: any) => {
      (job.job_tasks || []).forEach((jt: any) => {
        if (jt.assigned_machine_name) machineSet.add(jt.assigned_machine_name);
        if (jt.assigned_staff_name) staffSet.add(jt.assigned_staff_name);
      });
    });
    return {
      machineOptions: Array.from(machineSet).sort().map((n) => ({ value: n, label: n })),
      staffOptions: Array.from(staffSet).sort().map((n) => ({ value: n, label: n })),
    };
  }, [jobs]);

  // Filter jobs: search + machine + staff
  const filteredJobs = useMemo(() => {
    if (!jobs?.length) return [];
    let result = jobs as any[];
    if (jobSearch) {
      const q = jobSearch.toLowerCase();
      result = result.filter((job: any) =>
        (job.order_name || "").toLowerCase().includes(q) ||
        (job.part_name || "").toLowerCase().includes(q)
      );
    }
    if (machineFilter) {
      result = result.filter((job: any) =>
        (job.job_tasks || []).some((jt: any) => jt.assigned_machine_name === machineFilter)
      );
    }
    if (staffFilter) {
      result = result.filter((job: any) =>
        (job.job_tasks || []).some((jt: any) => jt.assigned_staff_name === staffFilter)
      );
    }
    return result;
  }, [jobs, jobSearch, machineFilter, staffFilter]);

  const ganttTasks = useMemo(() => {
    if (!filteredJobs?.length) return [];

    const tasks: any[] = [];
    let nextId = 1;

    filteredJobs.forEach((job: any) => {
      const scheduled = (job.job_tasks || [])
        .filter((jt: any) => jt.start_date && jt.status !== "completed")
        .sort(
          (a: any, b: any) =>
            new Date(a.start_date).getTime() -
            new Date(b.start_date).getTime()
        );

      if (scheduled.length === 0) return;

      const jobStart = new Date(scheduled[0].start_date);
      const jobEnd = scheduled.reduce((latest: Date, jt: any) => {
        const end = new Date(jt.end_date || jt.start_date);
        return end > latest ? end : latest;
      }, new Date(jobStart));

      const jobGanttId = nextId++;

      tasks.push({
        id: jobGanttId,
        text: `${job.order_name} \u2013 ${job.part_name}`,
        start: jobStart,
        end: jobEnd,
        progress: 0,
        type: "summary",
        parent: 0,
        open: true,
        dueDate: job.adjusted_required_completion_date,
      });

      scheduled.forEach((jt: any) => {
        const isSlotOnly =
          (jt.task_staff_capacity_method === "slot" &&
            jt.task_machine_method === "none") ||
          !jt.end_date ||
          jt.start_date === jt.end_date;

        const start = new Date(jt.start_date);
        const end = isSlotOnly
          ? new Date(jt.start_date)
          : new Date(jt.end_date);

        const label =
          jt.batch_index > 0
            ? `${jt.task_name} (Batch ${jt.batch_index})`
            : jt.task_name;

        tasks.push({
          id: nextId++,
          text: label,
          start,
          end,
          progress: Number(jt.percent_complete) || 0,
          type: isSlotOnly ? "milestone" : "task",
          parent: jobGanttId,
          taskCode: jt.task_task_id,
          staffName: jt.assigned_staff_name,
          machineName: jt.assigned_machine_name,
          latestStart: jt.latest_start_date,
        });
      });
    });

    return tasks;
  }, [filteredJobs]);

  const columns = [{ id: "text", header: "Job / Task", flexgrow: 1 }];
  const now = useNow(NOW_TICK_MS);
  const ganttDates = useMemo(
    () => ganttRange(ganttTasks, now, scaleView),
    [ganttTasks, now, scaleView]
  );

  // Searching centers on the matched jobs; otherwise the chart centers on now.
  const debouncedSearch = useDebouncedValue(jobSearch.trim(), 300);
  const focusMs = useMemo(
    () => (debouncedSearch ? focusDate(filteredJobs, Date.now()) : Date.now()),
    // filteredJobs follows the live search; only re-focus once typing settles.
    [debouncedSearch, machineFilter, staffFilter, scaleView, viewMode]
  );
  const focusRef = useRef(focusMs);
  focusRef.current = focusMs;
  const initGantt = useCallback((api: any) => centerGanttOn(api, new Date(focusRef.current)), []);

  const scrollToToday = useCallback(() => {
    if (viewMode === "Timeline") zoomRef.current?.scrollToToday?.();
    else if (apiRef.current) centerGanttOn(apiRef.current, new Date());
  }, [viewMode]);

  useEffect(() => {
    if (viewMode === "Gantt" && apiRef.current) centerGanttOn(apiRef.current, new Date(focusMs));
  }, [viewMode, focusMs]);

  if (isLoading) {
    return (
      <div className="w-full p-4">
        <Header title="Job Schedule" />
        <div style={{ textAlign: "center", padding: 80 }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  const hasData =
    viewMode === "Gantt" ? ganttTasks.length > 0 : filteredJobs.length > 0;

  if (!hasData) {
    return (
      <div className="w-full p-4">
        <Header title="Job Schedule" />
        <Empty
          description="No scheduled jobs found"
          style={{ marginTop: 80 }}
        />
      </div>
    );
  }

  return (
    <div className="w-full p-4">
      <Header title="Job Schedule" />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          marginBottom: 12,
        }}
      >
        <Input.Search
          placeholder="Search order or part..."
          allowClear
          style={{ width: 220 }}
          value={jobSearch}
          onChange={(e) => setJobSearch(e.target.value)}
          onSearch={(v) => setJobSearch(v)}
        />
        <Segmented
          options={["Timeline", "Gantt"]}
          value={viewMode}
          onChange={(val) => setViewMode(val as "Timeline" | "Gantt")}
        />
        <Radio.Group
          value={scaleView}
          onChange={(e) => setScaleView(e.target.value)}
          optionType="button"
          buttonStyle="solid"
          options={[
            { label: "Day", value: "day" },
            { label: "Week", value: "week" },
            { label: "Month", value: "month" },
          ]}
        />
        <Select
          placeholder="Filter by machine..."
          allowClear
          style={{ width: 180 }}
          value={machineFilter}
          onChange={(val) => setMachineFilter(val || null)}
          showSearch
          filterOption={(input, opt) => (opt?.label?.toString() || "").toLowerCase().includes(input.toLowerCase())}
          options={machineOptions}
        />
        <Select
          placeholder="Filter by staff..."
          allowClear
          style={{ width: 180 }}
          value={staffFilter}
          onChange={(val) => setStaffFilter(val || null)}
          showSearch
          filterOption={(input, opt) => (opt?.label?.toString() || "").toLowerCase().includes(input.toLowerCase())}
          options={staffOptions}
        />
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <Button size="small" onClick={scrollToToday}>
            Today
          </Button>
          {viewMode === "Timeline" && isZoomed && (
            <Button size="small" onClick={() => zoomRef.current?.reset()}>
              Reset Zoom
            </Button>
          )}
          <Dropdown
            menu={{
              items: [
                {
                  key: "snapshot",
                  icon: <DownloadOutlined />,
                  label: "Export Scheduler Snapshot",
                  onClick: async () => {
                    try {
                      const token = localStorage.getItem("auth_token");
                      const resp = await fetch(`${env.VITE_API_URL}/shop/api/scheduling/export/snapshot/`, {
                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                      });
                      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                      const blob = await resp.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `scheduler_snapshot_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, "")}.json`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch (err) {
                      console.error("Export failed:", err);
                    }
                  },
                },
              ],
            }}
            trigger={["click"]}
          >
            <Button icon={<MoreOutlined />} />
          </Dropdown>
        </div>
      </div>

      {viewMode === "Timeline" ? (
        <JobTimeline
          jobs={filteredJobs}
          scaleView={scaleView}
          onZoomChange={setIsZoomed}
          zoomRef={zoomRef}
          centerKey={`${debouncedSearch}|${machineFilter ?? ""}|${staffFilter ?? ""}`}
          focusMs={focusMs}
          nowMs={now}
        />
      ) : (
        <div style={{ height: "calc(100vh - 220px)" }}>
          <Willow>
            <Tooltip api={apiRef} content={TaskTooltip}>
              <Gantt
                ref={apiRef}
                init={initGantt}
                start={ganttDates.start}
                end={ganttDates.end}
                tasks={ganttTasks}
                scales={SCALE_PRESETS[scaleView]}
                columns={columns}
                cellHeight={32}
                readonly={true}
                zoom={true}
              />
            </Tooltip>
          </Willow>
        </div>
      )}
    </div>
  );
}
